import { useEffect } from 'react'
import { legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorSettingName } from '@/msgdeskRuntime/contracts/editorModel'
import { useEditorSurface } from '../../EditorSurfaceContext'
import { delegatedSettingChange, useSettingSelectGroups } from './SettingSelectState'
import {
  useProductDisplayCatalog,
  type ProductDisplayDefinition,
} from '../../ProductDisplayCatalogContext'
import type { RowVariantMetadataRegistry } from '../../../rowTemplates/RowTemplateTypes'
import type { SubsSection } from '@/msgdeskRuntime/state'
import { useEditorSession } from '../../EditorSessionContext'

interface DisplayOption {
  value: number
  text: string
  disabled?: boolean
}

const displaySelectGroups = [
  { master: 'is_view', combos: ['is_view_combo'] },
  { master: 'is_view_answers', combos: ['is_view_answers_combo'] },
  { master: 'is_view_msges', combos: ['is_view_msges_combo'] },
] as const

function rowOptions(registry: RowVariantMetadataRegistry | undefined, section: SubsSection): DisplayOption[] {
  return registry?.[section].variants.map(({ value, label }) => ({ value, text: label })) ?? []
}

function fallbackLabel(registry: RowVariantMetadataRegistry | undefined, section: SubsSection): string {
  if (!registry) return ''
  const fallback = registry[section].variants.find(choice => choice.variant === registry[section].fallback)
  return fallback?.label ?? 'local default'
}

export function DisplayPanel() {
  const { skin, creationProductPath } = useEditorSurface()
  const editorSession = useEditorSession()
  const productCatalog = useProductDisplayCatalog()
  const classes = skin.settings
  const { model, isEnabled, selectValue } = useSettingSelectGroups(displaySelectGroups)
  // Legacy hides the desk-type pair on update (m31 i<2: !is_update) and the
  // msg-row pair on non-msg desks (m31 i===3: is_msg). Inputs stay mounted so
  // the form contract and hydration keep every name addressable.
  const forcedProduct = creationProductPath
    ? productCatalog.find(product => product.path === creationProductPath)
    : undefined
  const hideDeskType = editorSession.mode === 'update_msg' || Boolean(forcedProduct)
  // Only a positively identified message owns submessage display settings.
  // Unknown/stale rough state therefore cannot expose this row on an answer.
  const hideMsgRows = editorSession.isMsg !== true
  const standardProduct = productCatalog.find(product => product.path === 'std')
  if (!standardProduct) throw new Error('Standard product Display definition is missing')

  const configuredValue = Number(model?.settings.is_view_combo)
  const configuredProduct = productCatalog.find(product => product.comboValue === configuredValue)
  const selectedProduct: ProductDisplayDefinition = forcedProduct
    ?? (isEnabled('is_view') && configuredProduct ? configuredProduct : standardProduct)
  const selectedRows = selectedProduct.rowVariants
  const deskTypeOptions: DisplayOption[] = productCatalog
    .filter(product => product.comboValue > 0 && !product.internalOnly)
    .map(product => ({
      value: product.comboValue,
      text: product.settingsSelectable ? product.label : `${product.label} (special create)`,
      disabled: !product.settingsSelectable,
    }))
  const answerOptions = rowOptions(selectedRows, 'answers')
  const messageOptions = rowOptions(selectedRows, 'submsges')
  const answerFallback = fallbackLabel(selectedRows, 'answers')
  const messageFallback = fallbackLabel(selectedRows, 'submsges')
  const creation = model?.productCreation ?? {}

  const setCreation = (values: Partial<NonNullable<typeof model>['productCreation']>) => {
    dispatchEditorAction({
      action: 'set-product-creation',
      values,
      reason: `product-display:${selectedProduct.path}:pre-create`,
    })
  }

  useEffect(() => {
    if (!model) return

    if (editorSession.isMsg === false && isEnabled('is_view_msges')) {
      dispatchEditorAction({
        action: 'set-more-setting',
        name: 'is_view_msges',
        value: false,
        reason: 'product-display:answer:submsges-unavailable',
      })
      return
    }

    if (!selectedRows) {
      for (const master of ['is_view_answers', 'is_view_msges'] as const) {
        if (!isEnabled(master)) continue
        dispatchEditorAction({
          action: 'set-more-setting',
          name: master,
          value: false,
          reason: `product-display:${selectedProduct.path}:rows-unavailable`,
        })
      }
    }

    // Deliberately NO stored-value normalization here: selecting a Message
    // type re-lists the two lower registries, but a stored choice foreign to
    // the selected registry stays untouched — it renders as the hidden
    // "Current setting (uses <fallback>)" option, and the save-side path
    // fallback resolves it (§0.12: unsupported values are not silently
    // rewritten). The former destructive rewrite clobbered the user's picked
    // Type of answers/msges the moment a product (or its Mailbox default on
    // first check) was selected.
  }, [editorSession.isMsg, model, selectedProduct.path, selectedRows])

  const row = (
    check: EditorSettingName,
    combo: EditorSettingName,
    label: string,
    options: DisplayOption[],
    hiddenRow: boolean,
    fallbackLabel = '',
  ) => {
    const currentValue = Number(model?.settings[combo])
    const keepsUnsupportedValue = Boolean(fallbackLabel) && Number.isInteger(currentValue) && currentValue > 0 && !options.some(option => option.value === currentValue)

    return (
      <div
        key={check}
        className={classes.row}
        hidden={hiddenRow}
        style={hiddenRow ? { display: 'none' } : undefined}
      >
        <label className={classes.rowCheck}>
          <input type="checkbox" name={check} value="1" checked={isEnabled(check)} onChange={delegatedSettingChange} />
          {label}
        </label>
        <select name={combo} className={classes.combo} disabled={!isEnabled(check)} value={selectValue(combo)} onChange={delegatedSettingChange}>
          {keepsUnsupportedValue ? (
            <option value={String(currentValue)} hidden>{`Current setting (uses ${fallbackLabel})`}</option>
          ) : null}
          {options.map(option => (
            <option key={option.value} value={String(option.value)} disabled={option.disabled}>{option.text}</option>
          ))}
        </select>
      </div>
    )
  }

  return (
    <fieldset {...legacyContractDataAttrs('editor.settings.main')} className={classes.panel}>
      <legend className={classes.legend}>Display</legend>
      {row('is_view', 'is_view_combo', 'Message type:', deskTypeOptions, hideDeskType, 'Standard')}
      {!hideDeskType && selectedProduct.createContract === 'poo-v1' ? (
        <div data-md-contract="product-create.poo-v1" className={classes.field}>
          <strong>Opening Proof Protocol settings</strong>
          <span>The OPP setup and protocol record will be created together when this new message or answer is saved. An edit PIN is mandatory; use the ordinary Protection tab. A parent-forced PIN already satisfies this rule.</span>
          <label>Public preview
            <textarea
              name="poo_preview"
              className={classes.textInput}
              value={creation.preview ?? ''}
              placeholder="Defaults to the first part of the document text"
              onChange={event => setCreation({ preview: event.target.value })}
            />
          </label>
          <label className={classes.check}>
            <input
              type="checkbox"
              name="poo_poa"
              checked={creation.poaEnabled === true}
              onChange={event => setCreation({ poaEnabled: event.target.checked })}
            />
            Require an Acceptance Proof after opening
          </label>
        </div>
      ) : null}
      {row('is_view_answers', 'is_view_answers_combo', 'Type of answers:', answerOptions, answerOptions.length === 0, answerFallback)}
      {row('is_view_msges', 'is_view_msges_combo', 'Type of msg:', messageOptions, hideMsgRows || messageOptions.length === 0, messageFallback)}
    </fieldset>
  )
}
